"use client";
import MainFilterSearchBox from "@/components/hotel-listing-v2/ui/SearchBar/MainFilterSearchBox";
import {
  HotelCard,
  TopHeaderFilter,
  SideBarFilters,
} from "@/components/hotel-listing-v2";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getHotels,
  getSearchPayload,
  hotels,
  loadMoreHotel,
  setCurrentBatchIndex,
  setSelectedBatch,
  getLoadingMore,
  saveSearchPayload,
  getHotelsProgress,
  Filter,
} from "@/lib/features/hotel/hotelSlice";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import {
  getUserCurrency,
  getUserCurrentCountry,
} from "@/lib/features/home/homeSlice";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { transformGuests } from "@/utils/helper";
import { ArrowClockwise } from "react-bootstrap-icons";
import styles from "@/components/ui/Loader.module.scss";
import useMediaQuery from "@/hooks/useMediaQuery";
import { Progress } from "reactstrap";
import { makeQueryString, parseQueryString } from "@/utils/queryString";
import { throttle } from "lodash-es";
import { produce } from "immer";
import "./HotelListingV2.css";
import { mealPlanMapping } from "@/utils/mealPlanUtils";
import { trackEvent, VIEW_ITEM_LIST_EVENT } from "@/utils/gtm";
import IdleTimeoutModal from "@/components/ui/IdleTimeoutModal";

// How early (in px before the sentinel enters the viewport) the next hotel
// batch starts loading. Generous so rates are usually ready before the user
// reaches the bottom of the list.
const BATCH_PREFETCH_ROOT_MARGIN = "900px 0px";

export const HotelListingV2 = () => {
  const [scrollY, setScrollY] = useState(0);

  const [isSidebarFixed, setIsSidebarFixed] = useState<boolean>(false);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const nextBatchIndexRef = useRef(0);
  const batchLoadPromiseRef = useRef<Promise<void> | null>(null);
  const sentinelInViewRef = useRef(false);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const lastSearchSignatureRef = useRef("");
  const hotelResultsRef = useRef<HTMLDivElement | null>(null);
  const [searchSubmitted, setSearchSubmitted] = useState<boolean>(false);

  const router = useRouter();
  const dispatch = useAppDispatch();
  const queryParams: any = useSearchParams();

  // Parse the query string into a payload using parseQueryString
  const payloadFromQuery: any = useMemo(() => {
    if (!queryParams) return null; // Handle case where searchParams isn't ready
    return parseQueryString(queryParams);
  }, [queryParams]);

  const id = payloadFromQuery && payloadFromQuery?.location?.code;
  const activeSearchSignature = useMemo(
    () => JSON.stringify(payloadFromQuery || {}),
    [payloadFromQuery]
  );

  const userCurrency = useAppSelector(getUserCurrency);
  const userCurrentCountry = useAppSelector(getUserCurrentCountry);
  const searchPayload = useAppSelector(getSearchPayload);
  const loadingMore = useAppSelector(getLoadingMore);

  const [hoveredHotelId, setHoveredHotelId] = useState<number | null>(null);
  const [selectedHotel, setSelectedHotel] = useState<any>();

  const hotelsData = useAppSelector(hotels);

  const allHotels = hotelsData && hotelsData[id];
  const activeSearchPayload = id ? searchPayload?.[id] : undefined;
  const currentBatchIndex = allHotels?.currentBatchIndex || 0;
  const selectedBatch = allHotels?.selectedBatch;
  const totalLoaded: number = useAppSelector(getHotelsProgress);

  const isDesktop = useMediaQuery("(min-width: 992px)");
  const [isSearchModalOpen, setIsSearchModalOpen] = useState<boolean>(false);
  const currentPage = usePathname();

  const [hotelsState, setHotelsState] = useState<any>({});
  const remainingBatchCount = allHotels?.remaining_batches?.length || 0;
  const hasVisibleHotels =
    (hotelsState?.hotels?.length || allHotels?.hotels?.length || 0) > 0;
  const hasMoreBatches = currentBatchIndex < remainingBatchCount;

  const openSearchModal = useCallback(
    (e: any) => {
      console.log("isDesktop", isDesktop, currentPage);
      // e.stopPropagation();
      if (isDesktop == false) {
        setIsSearchModalOpen(true);
      } else {
        setIsSearchModalOpen(false);
      }
    },
    [isDesktop, currentPage]
  );

  // Effect to close modal when switching to desktop mode
  useEffect(() => {
    if (isDesktop && isSearchModalOpen) {
      setIsSearchModalOpen(false); // Close modal when screen becomes desktop
    }
  }, [isDesktop, isSearchModalOpen]);

  /*
  Function to filter hotels based on the filters applied by the user 
  */
  const onFilter = useCallback(
    (filters: Filter[]) => {
      //on page load if data is present in redux and not filters or sorting applied then update local state only
      if (allHotels && filters?.length == 0) {
        console.log("allHotels", allHotels);
        // console.log("on page load if data is present in redux and not filters or sorting applied then update local state only")
        setHotelsState(allHotels);
      }
      //data is present in redux and filters or sorting applied then update local state
      else {
        setIsFiltering(true);
        // console.log("onFilter Applied", filters);
        // const { max } = userCurrency;
        const { hotels, ...rest } = allHotels;
        let filteredHotels =
          allHotels?.hotels?.length > 0 &&
          allHotels?.hotels?.filter((hotel: any) => {
            return filters?.every((filter: any) => {
              switch (filter?.filterType) {
                case "price":
                  const price = hotel?.min_rate?.priceWithMarkup;
                  const priceFilter = filter.value as {
                    from: number;
                    to: number;
                    max?: number;
                  };
                  if (!priceFilter || typeof price !== "number") return true;

                  const { from, to, max } = priceFilter;
                  console.log("priceFilter:", priceFilter, "price:", price);

                  return to === max
                    ? price >= from // Full range
                    : price >= from && price <= to; // Partial range

                /*const price = hotel?.min_rate?.priceWithMarkup;
                  const priceFilter = filter?.value;
                  const { from, to, max } = priceFilter;
                  const maxAllowed = max;
                  console.log("priceFilter", priceFilter)
                  const condition =
                    to == maxAllowed
                      ? price >= from && (price <= to || price > maxAllowed)
                      : price >= from && price <= to;
                      console.log("condition", priceFilter?.value, condition)

                  return condition;*/

                case "amenities":
                  const amenitiesFilterValue = filter?.value || "";
                  return amenitiesFilterValue?.length === 0
                    ? true
                    : amenitiesFilterValue.some((data: string) =>
                        hotel?.min_rate?.other_inclusions?.includes(data)
                      );

               /* case "meal_plan":
                  const mealPlanFilter = filter?.value || [];

                  return mealPlanFilter?.length === 0
                    ? true
                    : mealPlanFilter.some((meal: string) =>
                        hotel?.min_rate?.boarding_details?.includes(meal)
                      );*/
              case "meal_plan":
                const mealPlan = filter?.value || [];
                if (mealPlan && mealPlan.length > 0 && Array.isArray(mealPlan)) {
                  return mealPlan.some((plan: string) =>
                    mealPlanMapping[plan]?.includes(hotel?.min_rate?.boarding_details?.[0] || "")
                  );
                } else if (mealPlan && typeof mealPlan === "string" && mealPlan !== "") {
                  return mealPlanMapping[mealPlan]?.includes(hotel?.min_rate?.boarding_details?.[0] || "");
                }
                return true;

                case "rating":
                  const ratingFilter = filter?.value || [];
                  if (ratingFilter.length === 0) return true;

                  const hotelRating = hotel?.category; // Could be number or string
                  const isUnrated = ratingFilter.includes(0); // Check if "Unrated" is selected

                  // If "Unrated" is selected, match hotels with category 0 or ""
                  if (isUnrated && (hotelRating === 0 || hotelRating === "")) {
                    return true;
                  }

                  // Otherwise, match numeric ratings
                  return ratingFilter.includes(Number(hotelRating));

                case "property_type":
                  // console.log("propertyTypeFilter", filter?.value);
                  const propertyTypeFilter = filter?.value || [];
                  return propertyTypeFilter?.length === 0
                    ? true
                    : propertyTypeFilter.some(
                        (data: string) =>
                          hotel?.accommodation_type_sub_name == data
                      );

                default:
                  return true; // If no matching filter type, just return true (i.e., no filtering on this criteria)
              }
            });
          });
        // console.log("before Sort", filteredHotels);
        // Apply sorting based on the "sortBy" filter
        // Apply sorting based on the "sortBy" filter
        const sortByFilter = filters.find(
          (f: Filter) => f.filterType === "sortBy"
        );
        const sortByFilterValue = (sortByFilter?.value as { value: string })
          ?.value;
        // console.log("sortByFilter", sortByFilter)
        // console.log("sortByFilterValue", sortByFilterValue)
        // console.log("sortByFilterValue", filteredHotels)
        if (sortByFilter && sortByFilterValue && filteredHotels) {
          switch (sortByFilterValue) {
            case "price_asc":
              filteredHotels.sort(
                (a: any, b: any) =>
                  a?.min_rate?.priceWithMarkup - b?.min_rate?.priceWithMarkup
              );
              break;
            case "price_desc":
              filteredHotels.sort(
                (a: any, b: any) =>
                  b?.min_rate?.priceWithMarkup - a?.min_rate?.priceWithMarkup
              );
              break;
            case "rating_desc":
              filteredHotels.sort(
                (a: any, b: any) => b?.category - a?.category
              );
              break;
            default:
              break;
          }
        }

        // const finalFiltered = {
        //   ...rest,
        //   hotels: filteredHotels,
        //   no_of_hotels: filteredHotels?.length,
        //   filters,
        // };
        // setHotelsState(finalFiltered);
        // Update state

        // Create the next state

        console.log("allHotels", allHotels);
        //Update Local State With Filtered Data
        setTimeout(() => {
          setIsFiltering(false);
          setHotelsState(
            produce(allHotels, (_: any) => {
              _.hotels = filteredHotels;
              _.no_of_hotels = filteredHotels?.length;
              _.appliedFilters = filters;
            })
          );
        }, 0);
      }
    },
    [allHotels, userCurrency]
  );

  // Trigger initial filtering on mount or when allHotels changes
  useEffect(() => {
    // console.log("allHotels", allHotels);
    if (allHotels && allHotels.hotels?.length > 0) {
      console.log("allHotels.appliedFilters", allHotels.hotels);
      onFilter(
        allHotels?.appliedFilters?.length > 0 ? allHotels?.appliedFilters : []
      );
    }
    else{
      //Reset local state with empty hotels array
      setHotelsState((_: any) => ({
        hotels: [],
        no_of_hotels: 0
      }));
    }
  }, [allHotels]);
  //[allHotels, hotelsState, onFilter]);
  const onSearch = useCallback(() => {
    setIsSearchModalOpen(false);
    setSearchSubmitted(true);
  }, []);

  /*
  Function is used to call the API with the batch items.
  It returns the status and data of the API response. 
  */
  const callApiWithBatch = useCallback(
    async (batchItems: string[], index: number) => {
      if (
        !activeSearchPayload?.location ||
        !activeSearchPayload?.dates ||
        !activeSearchPayload?.guests
      ) {
        return {
          status: 400,
          data: { hotels: [], no_availability: false },
        };
      }
      const { location, dates, guests } = activeSearchPayload;
      const requestCurrency =
        userCurrency?.code || activeSearchPayload?.currency || "USD";
      const requestNationality =
        userCurrentCountry || activeSearchPayload?.nationality || "US";
      const result = await dispatch(
        loadMoreHotel({
          url: "v1/hotels/batch-data",
          payload: {
            code: location?.code,
            type: location?.type,
            checkin: dates && dates[0],
            checkout: dates && dates[1],
            rooms: transformGuests(guests),
            rates: "concise",
            currency: requestCurrency,
            nationality: requestNationality,
            hotel_codes: batchItems,
            method: "batch",
            index: index,
          },
        })
      ).unwrap();
      return result;
    },
    [activeSearchPayload, dispatch, id, userCurrency?.code, userCurrentCountry]
  );
  /*
  Batch loading - single mechanism, single owner.

  History: this listing previously ran FOUR competing load triggers
  (react-infinite-scroll-component, a hand-rolled window scroll listener,
  a background drain-all loop, and a recursive auto-chain inside the load
  function), coordinating through shared ref flags and Redux round-trips.
  The library only re-arms when dataLength changes, so a batch yielding
  zero available hotels froze it permanently; the drain loop captured a
  stale batch index; and the triggers raced each other differently per
  browser - the source of "sometimes works on Safari, breaks on Chrome".

  Now: one IntersectionObserver on a sentinel below the list prefetches
  the next batch early, a single-flight sequential loader owns the batch
  index locally, zero-yield batches drain forward while the sentinel is
  still visible, and a manual "Load more hotels" button always renders as
  a fallback so users are never stranded even if observation fails.
  */

  // Latest-value snapshot so the loader never acts on stale closures.
  const latestBatchStateRef = useRef({
    remainingBatches: [] as any[],
    loadingMore: false,
    currentBatchIndex: 0,
  });
  latestBatchStateRef.current = {
    remainingBatches: allHotels?.remaining_batches || [],
    loadingMore,
    currentBatchIndex,
  };

  const loadNextBatch = useCallback((): Promise<void> => {
    if (batchLoadPromiseRef.current) {
      return batchLoadPromiseRef.current;
    }

    const run = async () => {
      // Never race the initial search request.
      if (latestBatchStateRef.current.loadingMore) {
        return;
      }
      // Re-sync if Redux (e.g. a restored session) is ahead of us.
      if (
        nextBatchIndexRef.current <
        latestBatchStateRef.current.currentBatchIndex
      ) {
        nextBatchIndexRef.current =
          latestBatchStateRef.current.currentBatchIndex;
      }

      // Sequential drain: keep going past batches that yield no available
      // hotels while the sentinel is still on screen - the list didn't
      // grow, so no new intersection event will arrive to re-trigger us.
      for (;;) {
        const index = nextBatchIndexRef.current;
        const remaining = latestBatchStateRef.current.remainingBatches;
        if (index >= remaining.length) {
          return;
        }

        const batchEntry = remaining[index] || {};
        const batchKey = Object.keys(batchEntry)[0]; // e.g. "batch_1"
        const batchItems: string[] = batchKey ? batchEntry[batchKey] : [];
        nextBatchIndexRef.current = index + 1;
        if (!batchItems?.length) {
          continue;
        }

        dispatch(
          setSelectedBatch({ hotel_code: id, batch: batchItems } as any)
        );
        dispatch(
          setCurrentBatchIndex({ hotel_code: id, index: index + 1 } as any)
        );

        const result = await callApiWithBatch(batchItems, index + 1);
        const status = result?.status;
        const data = result?.data;
        const yieldedNothing =
          status === 200 &&
          (data?.no_availability === true ||
            (Array.isArray(data?.hotels) && data.hotels.length === 0));

        if (!yieldedNothing || !sentinelInViewRef.current) {
          return;
        }
      }
    };

    const promise = run().finally(() => {
      batchLoadPromiseRef.current = null;
    });
    batchLoadPromiseRef.current = promise;
    return promise;
  }, [callApiWithBatch, dispatch, id]);

  // A new search starts its batches from the top.
  useEffect(() => {
    nextBatchIndexRef.current = 0;
  }, [id, activeSearchSignature]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMoreBatches) {
      sentinelInViewRef.current = false;
      return;
    }
    if (typeof IntersectionObserver === "undefined") {
      // Very old browsers: the "Load more hotels" button still works.
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        sentinelInViewRef.current = Boolean(entry?.isIntersecting);
        if (entry?.isIntersecting) {
          void loadNextBatch();
        }
      },
      { root: null, rootMargin: BATCH_PREFETCH_ROOT_MARGIN, threshold: 0 }
    );
    observer.observe(sentinel);

    return () => {
      sentinelInViewRef.current = false;
      observer.disconnect();
    };
  }, [hasMoreBatches, loadNextBatch, activeSearchSignature]);

  useEffect(() => {
    // console.log("On Page Load From Hotel Listing", payloadFromQuery);
    async function getData() {
      const payload: any = payloadFromQuery;
      const searchSignature = JSON.stringify(payload || {});
      if (lastSearchSignatureRef.current === searchSignature) {
        return;
      }
      lastSearchSignatureRef.current = searchSignature;
      // console.log("On page Change", payload);
      const isSearchPayloadSaved = await dispatch(
        saveSearchPayload(payload)
      ).unwrap();

      if (isSearchPayloadSaved) {
        const {
          location,
          dates,
          guests,
          property_name,
          property_code,
          city_code,
          geolocation,
          radius,
          north,
          east,
          south,
          west,
        } = payload;
        const requestCurrency = userCurrency?.code || payload?.currency || "USD";
        const requestNationality = userCurrentCountry || payload?.nationality || "US";
        if (geolocation && radius) {
          dispatch(
            getHotels({
              url: "v1/hotels/first-available-batch",
              payload: {
                query: payload,
                type: "geolocation",
                checkin: dates && dates[0],
                checkout: dates && dates[1],
                rooms: JSON.stringify(transformGuests(guests)),
                rates: "concise",
                currency: requestCurrency,
                nationality: requestNationality,
                radius,
                north,
                east,
                south,
                west,
              },
            })
          );
        } else if (property_code && property_name && city_code) {
          dispatch(
            getHotels({
              url: "v1/hotels/first-available-batch",
              payload: {
                code: property_code,
                type: "hotel",
                checkin: dates && dates[0],
                checkout: dates && dates[1],
                rooms: JSON.stringify(transformGuests(guests)),
                rates: "concise",
                currency: requestCurrency,
                nationality: requestNationality,
                reset: true,
                city_code,
              },
            })
          );
        } else {
          const { data } = await dispatch(
            getHotels({
              url: "v1/hotels/first-available-batch",
              payload: {
                code: location?.code,
                type: location?.type,
                checkin: dates && dates[0],
                checkout: dates && dates[1],
                rooms: JSON.stringify(transformGuests(guests)),
                rates: "concise",
                currency: requestCurrency,
                nationality: requestNationality,
              },
            })
          ).unwrap();
          // console.log("GTM", data)
          // const itemsData = data?.hotels?.map((data: any) => _.pick(data, ['name', 'hotel_code','geolocation','country','city','address','category','country_code','city_code']));
          const itemsData = data?.hotels?.map((data: any, index: number) => ({
            item_id:  data?.hotel_code,
            item_name: data?.name,
            price: data?.min_rate?.price,
            markup: data?.markup,  
            index,
            item_category: data?.accommodation_type_sub_name,
            location_id: data?.city_code,
          }));
          const gtmData = {
            ecommerce: {
              item_list_id: "search_result_hotels",
              item_list_name: "Search Results - Hotels",
              items: itemsData
            }
          }
          // console.log("gtmData", gtmData)
          trackEvent(VIEW_ITEM_LIST_EVENT, gtmData);
        }
      }
    }
    getData();
  }, [payloadFromQuery]);

  const closeModal = useCallback((e: any) => {
    setIsSearchModalOpen(false);
    e.stopPropagation();
  }, []);

  // Scroll handler to toggle fixed sidebar
  /*const handleScroll = useCallback(() => {
    const section = document.querySelector(".layout-pt-md.layout-pb-lg");
    if (!section) return;

    const rect = section.getBoundingClientRect();
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const sectionTop = rect.top + scrollTop;

    setIsSidebarFixed(scrollTop >= sectionTop);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);*/

  // Replace your existing handleScroll with this optimized version
  const handleScroll = useCallback(
    throttle(() => {
      const section = document.querySelector(".layout-pt-md.layout-pb-lg");
      if (!section) return;
      const currentScrollY =
        window.scrollY || document.documentElement.scrollTop;
      setScrollY(currentScrollY);
    }, 50),
    [isSidebarFixed]
  );

  // Update your useEffect for scroll listener
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      (handleScroll as any).cancel(); // Cancel the throttle
    };
  }, [handleScroll]);

  return (
    <>

      <section className="pt-40 pb-40 bg-light-2">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="text-center">
                <h1 className="text-30 fw-600">Find Your Dream Luxury Hotel</h1>
              </div>
              {/* End text-center */}
              <MainFilterSearchBox requestFrom={"hotels"} />
              {totalLoaded > 0 && (
                <div className="row justify-between items-center pt-20 mb-10">
                  <div className="col-sm-12">
                    <Progress value={totalLoaded} max={100} color={"#9c7d5c"}>
                      {parseFloat(totalLoaded.toFixed(0))}%
                      {totalLoaded > 90 && " You‘re almost there!"}
                    </Progress>
                  </div>
                </div>
              )}
            </div>
            {/* End col-12 */}
          </div>
        </div>
      </section>

      <section className="layout-pt-md layout-pb-lg">
        <div className="container">
          <div className="row y-gap-30">
            <div className={`col-xl-3 `}>
              <aside
                className={`sidebar y-gap-40 xl:d-none ${
                  scrollY > 300 ? "fixed_sidebar" : ""
                }`}
              >
                <SideBarFilters
                  hotelCode={id}
                  onFilter={onFilter}
                  onSearch={searchSubmitted}
                  allAmenities={allHotels?.allAmenities}
                  allMealPlan={allHotels?.allMealPlan}
                  hotels={hotelsState}
                  propertyTypes={allHotels?.propertyTypes}
                />
              </aside>
              {/* End sidebar for desktop */}

              <div
                className="offcanvas offcanvas-start"
                tabIndex={-1}
                id="listingSidebar"
              >
                <div className="offcanvas-header">
                  <h5 className="offcanvas-title" id="offcanvasLabel">
                    Filter Hotels
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="offcanvas"
                    aria-label="Close"
                  ></button>
                </div>
                {/* End offcanvas header */}

                <div className="offcanvas-body">
                  <aside className="sidebar y-gap-40  xl:d-block">
                    <SideBarFilters
                      hotelCode={id}
                      onFilter={onFilter}
                      onSearch={searchSubmitted}
                      allAmenities={allHotels?.allAmenities}
                      allMealPlan={allHotels?.allMealPlan}
                      hotels={hotelsState}
                      propertyTypes={allHotels?.propertyTypes}
                    />
                  </aside>
                </div>
                {/* End offcanvas body */}
              </div>

              {/* End mobile menu sidebar */}
            </div>
            {/* End col */}

            <div className={`col-xl-9`} ref={hotelResultsRef}>
              <TopHeaderFilter hotels={hotelsState} />
              <div className="mt-30"></div>
            
             
              <div className="row y-gap-30">
                {/* {JSON.stringify(hotelsState?.appliedFilters)} */}
                <HotelCard
                  hotels={hotelsState}
                  onHover={setHoveredHotelId}
                  onClick={setSelectedHotel}
                />
              </div>
              {/* Sentinel for the IntersectionObserver batch loader - always
                  rendered so the observer effect can attach as soon as more
                  batches become available. */}
              <div ref={loadMoreSentinelRef} aria-hidden="true" />
              {loadingMore && hasVisibleHotels && (
                <div className="col-lg-12 d-flex justify-content-center">
                  <div className="py-10 px-20">
                    We are fetching hotels, please wait{" "}
                    <ArrowClockwise size={20} className={styles?.spinner} />
                  </div>
                </div>
              )}
              {!loadingMore && hasVisibleHotels && hasMoreBatches && (
                <div className="col-lg-12 d-flex justify-content-center">
                  <button
                    type="button"
                    className="button h-50 px-24 -dark-1 bg-blue-1 text-white"
                    onClick={() => void loadNextBatch()}
                  >
                    Load more hotels
                  </button>
                </div>
              )}
              {!loadingMore && hasVisibleHotels && !hasMoreBatches && (
                <div className="col-lg-12 d-flex justify-content-center">
                  <div className="py-10 px-20">
                    These are all the hotels we found for your search.
                  </div>
                </div>
              )}
              {/* End .row */}
              {/* <Pagination /> */}
            </div>
            {/* End .col for right content */}
          </div>
          {/* End .row */}
        </div>
        {/* End .container */}
      </section>
      <IdleTimeoutModal/>
    </>
  );
};
